import { ReactNode } from 'react';
import { IPlayer } from 'providers/player/abstract/IPlayer';
import { AiringTime, Recordable, Rentable, Subscribable } from './CommonTypes';
import { Marker, Person } from './Asset';

export type PlayerLocationParams = {
    channelId?: string;
    broadcastId?: string;
    assetId?: string;
    recordingId?: string;
};

export type PlayerProps = {
    type: PlayingAssetType;
    params: PlayerLocationParams;
};

export type Track = {
    role: string;
    language: string;
    label: string;
    channelsCount?: number;
    raw?: any;
};

export type BitrateTrack = {
    height: number;
    width: number;
    bandwidth: any;
    raw?: any;
};

export enum PlayerEvents {
    BUFFERING = 'buffering',
    WAITING = 'waiting',
    VOLUME_CHANGE = 'volumechange',
    PLAY = 'play',
    BUFFER_END = 'bufferEnd',
    PLAYING = 'playing',
    PAUSE = 'pause',
    ENDED = 'ended',
    TRACKS_CHANGED = 'trackschanged',
    TIME_UPDATE = 'timeupdate',
    END_OF_STREAM = 'end_of_stream',
    JUMP_TO_LIVE = 'jump_to_live',
    CAST_STATUS_CHANGED = 'caststatuschanged',
    MEDIA_CHANGED = 'media_changed',
    END_OF_PROGRAM = 'end_of_program',
    TOO_MANY_DEVICES = 'too_many_devices',
    INFO_REQUESTED = 'info_requested',
}

export type PlayerAsset = {
    stream_url: string;
    id: string;
    manifestUrl: string;
    hasStream: boolean;
    contentId: string;
    keepAliveInterval: number;
    sessionId: string;
    image: string;
    drm_license_url?: string;
    drm_license_context?: string;
    analytics_npaw?: NpawAnalytics;
};

export type PlaybackSession = {
    manifestUrl: string;
    keepAliveInterval: number;
    sessionId: string;
};

export type PlaybackPositionsResponse = {
    listing_playback_positions: Record<string, number>;
    vod_asset_playback_positions: Record<string, number>;
};

export enum PlayingAssetType {
    LIVE = 'LIVE',
    CATCHUP = 'catchup',
    VOD = 'vod',
    TRAILER = 'trailer',
    RECORDING = 'recording',
    REPLAY = 'REPLAY',
    LISTING = 'LISTING',
}

export enum PlayingMode {
    NORMAL = 'normal',
    TIME_SHIFT = 'time_shift',
}

export enum PlayerControl {
    JUMP_BACK = 'jump-back',
    JUMP_FORWARD = 'jump-fwd',
    PLAY_PAUSE = 'play-pause',
    CAST = 'cast',
    MORE_MEDIA = 'more-media',
    NEXT_ASSET = 'next-asset',
    SUBTITLE = 'subtitle',
    EPISODES = 'episodes',
    FULL_SCREEN = 'fullscreen',
    VOLUME = 'volume',
    INFO = 'info',
    GO_TO_LIVE = 'go_to_live',
    CLOSE = 'close',
    MINIMIZE = 'minimize',
    SEEKBAR = 'seekbar',
}

export enum PlayerIcon {
    CATCHUP = 'catchUpIcon',
    ONGOING_RECORDING = 'icoOngoingRecording',
    SCHEDULED_RECORDING = 'icoScheduledRecording',
    RECORDED_RECORDING = 'icoRecordedRecording',
}

type BookmarkPlayerAssetInfo = {
    vodId: string;
    duration: number;
    bookmark?: number;
};

export type BroadcastPlayerAsset = PlayerAsset &
    AiringTime &
    BookmarkPlayerAssetInfo &
    Recordable & {
        type: PlayingAssetType;
        title: string;
        subtitle?: string;
        episode_title?: string;
        id: string;
        listingId: string;
        program?: any;
        logicalChannelNumber: string;
        channelId: string;
        channel_id: string;
        channelLogo: string;
        channelName: string;
        channelProductCode: string;
        releaseYear?: string;
        countries?: string[];
        channelHasReplay: boolean;
        channelSubscribed: boolean;
        recording_id?: string;
        seriesId?: string;
        genres: any[];
        cast: Person[];
        captionLanguages?: string[];
        originalLanguages?: string[];
        description: string;
        isCurrentlyRecording?: boolean;
    };

export type LivePlayerAssetInfo = BroadcastPlayerAsset &
    Subscribable &
    BookmarkPlayerAssetInfo & {
        isLive: boolean;
        type: string;
        replayContentId: string;
        pre_padding?: number;
        post_padding?: number;
        use_case?: string;
        channel_id?: string;
        startTime?: Date;
        endTime?: Date;
        heartbeat_interval?: number;
        seasonNumber?: number;
        episodeNumber?: number;
        recordingUntil?: number | null;
        replayUntil?: number | null;
    };

export type CatchupPlayerAssetInfo = BroadcastPlayerAsset &
    BookmarkPlayerAssetInfo & {
        listing_id?: string;
        pre_padding?: number;
        post_padding?: number;
        use_case?: string;
        heartbeat_interval?: number;
        seasonNumber?: number;
        episodeNumber?: number;
    };

export type TrailerPlayerAssetInfo = PlayerAsset & {
    type: PlayingAssetType;
    id: string;
    title: string;
    genres: any[];
    subtitle?: string;
    episode_title?: string;
    releaseYear?: string;
    countries?: string[];
    channelLogo?: string;
    cast: Person[];
    captionLanguages?: string[];
    originalLanguages?: string[];
    description: string;
    listingId?: number;
    duration: number;
    use_case?: string;
    startTime?: number;
    endTime?: number;
    heartbeat_interval?: number;
    bookmark?: number;
    seasonNumber?: number;
    episodeNumber?: number;
};

export type VodPlayerAssetInfo = PlayerAsset &
    BookmarkPlayerAssetInfo &
    Rentable & {
        type: PlayingAssetType;
        id: string;
        title: string;
        titleId: string;
        subtitle?: string;
        episode_title?: string;
        releaseYear?: string;
        countries?: string[];
        isMovie: boolean;
        duration: number;
        listingId?: number;
        seriesId?: string;
        seasonNumber?: number;
        episodeNumber?: number;
        channelLogo?: string;
        genres: any[];
        cast: Person[];
        captionLanguages?: string[];
        originalLanguages?: string[];
        description: string;
        use_case?: string;
        startTime?: number;
        endTime?: number;
        heartbeat_interval?: number;
        vodSeriesId?: string;
    };

export type RecordingPlayerAssetInfo = Omit<
    CatchupPlayerAssetInfo,
    'channelSubscribed' | 'channelHasReplay' | 'channelProductCode' | 'isRecordingSubscribed' | 'recordingProductCode'
>;

export type PlayingAsset =
    LivePlayerAssetInfo | CatchupPlayerAssetInfo | RecordingPlayerAssetInfo | TrailerPlayerAssetInfo | VodPlayerAssetInfo;

export type PlayerError = {
    code: any;
    category?: any;
    message?: string;
    linkClickListener?: (e) => void;
    devMessage?: string;
    details?: { [key: string]: string };
};
export type PlayerConfig = {
    errorHandler: (error: PlayerError) => void;
    onInitialized: (instance: IPlayer) => void;
    onNetworkError: () => void;
    onStreamRecovered?: () => void;
};

export const SUBTITLE_NONE = 'NONE';
export const SUBTITLE_OFF = 'off';
export const BITRATE_TRACK_AUTO = 'AUTO';
export const AUDIO_DEFAULT = 'DEFAULT';

export type PropTypesPlayerUICommon = {
    isFullScreen: boolean;
    shouldHideLayout: boolean;
    loading: boolean;
    hasTracks: boolean;
    casting: boolean;
    playingMode: PlayingMode;
    onControlsRef: (ref: HTMLDivElement) => void;
    onBackClicked: () => void;
    toggleFullScreen: () => void;
    forceExcludedControls?: string[];
    extraControls?: ReactNode;
    isEmbedded?: boolean;
    setLayoutHidden: (isHidden: boolean) => void;
    holdLayout: (hold: boolean) => void;
    hideLayoutNow: () => void;
};

export type PropTypesAdditional = {
    metaData: string[];
    metaIcons: PlayerIcon[];
    moreOverlayMetaData: {
        firstLine: string[];
        secondLine: string[];
    };
    contentMarker: Marker;
    controls: PlayerControl[];
    disabledControls: PlayerControl[];
    logo?: ReactNode;
    seekEnabled: boolean;
    title: string;
    subtitle: string;
    actionPopup?: {
        [key in PlayerControl]?: ReactNode;
    };
    mobileOverlayActionHandlers?: ReactNode[];
    logicalChannelNumber?: string;
    description: string;
    cast: Person[];
    originalLanguages?: string[];
    captionLanguages?: string[];
};

export enum PlayerKeyboardControl {
    SPACE = 'Space',
    LEFTARROW = 'ArrowLeft',
    RIGHTARROW = 'ArrowRight',
    UPARROW = 'ArrowUp',
    DOWNARROW = 'ArrowDown',
    ESCAPE = 'Escape',
    F = 'KeyF',
    M = 'KeyM',
}

export type NpawAnalytics = {
    'content.transactionCode'?: string;
    'content.customDimension.1'?: string;
};

export type StreamSessionResponse = {
    use_case: string;
    stream_url: string;
    drm_license_url: string;
    drm: string;
    stream_format: string;
    heartbeat_interval: number;
    id: string;
    thumbnails: boolean;
    vod_asset_id: string;
    analytics_npaw?: NpawAnalytics;
};

export type ActiveSessionsResponse = {
    count: number;
    limit: number;
    sessions: {
        app_name: string;
        created_at: number;
        device_id: string;
        device_name: string;
        id: string;
        use_case: string;
    }[];
};

export type StreamSessionsDetailsResponse = {
    browser: string;
    client_id: string;
    device_type: string;
    device_uuid: string;
    device_family?: string;
    device_name?: string;
    id: string;
    ip_address: string;
    last_activity: number;
    os_version: string;
    os: string;
    screen_type?: string;
    scope?: string;
    displayed_device_name?: string;
    is_removable?: boolean;
};
